import React from 'react';
import { TrendingUp, BarChart3, Activity, Zap, Target, ArrowRight, CheckCircle } from 'lucide-react';

const InveskitLanding = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Inveskit</h1>
              <p className="text-xs text-gray-500">스마트 투자 분석 도구</p>
            </div>
          </div>
          <a 
            href="https://www.inveskit.site" 
            target="_blank"
            rel="noopener noreferrer"
            className="bg-slate-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
          >
            시작하기
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 py-20 text-center">
        <div className="inline-block bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
          AI 기반 투자 분석 서비스
        </div>
        <h2 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
          AI가 분석하는<br />나의 투자 습관
        </h2>
        <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
          주식 거래 내역을 입력하면 AI가 당신의 매매 패턴을 분석하고<br />
          볼린저 밴드, 추세추종 등 다양한 전략 기반 조언을 제공합니다
        </p>
        <div className="flex gap-4 justify-center">
          <a 
            href="https://www.inveskit.site" 
            target="_blank"
            rel="noopener noreferrer"
            className="bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-slate-800 transition-all hover:scale-105 flex items-center gap-2 shadow-lg"
          >
            무료로 시작하기
            <ArrowRight className="w-5 h-5" />
          </a>
          <a 
            href="#features"
            className="bg-white text-slate-900 px-8 py-4 rounded-lg font-semibold text-lg border-2 border-slate-900 hover:bg-slate-50 transition-colors"
          >
            더 알아보기
          </a>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            왜 Inveskit인가요?
          </h3>
          <p className="text-gray-600 text-lg">
            투자 분석을 위한 모든 기능을 하나의 플랫폼에서
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-2xl border border-gray-200 hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
              <BarChart3 className="w-7 h-7 text-blue-600" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-3">거래 내역 관리</h4>
            <p className="text-gray-600 leading-relaxed">
              종목 검색 자동완성, 실시간 데이터 연동으로 손쉽게 거래 내역을 기록하고 관리하세요
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-gray-200 hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-6">
              <Activity className="w-7 h-7 text-emerald-600" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-3">실시간 차트 분석</h4>
            <p className="text-gray-600 leading-relaxed">
              60일 주가 데이터와 매매 시점을 시각화하여 투자 패턴을 한눈에 파악하세요
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-gray-200 hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
              <Zap className="w-7 h-7 text-purple-600" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-3">AI 투자 조언</h4>
            <p className="text-gray-600 leading-relaxed">
              볼린저 밴드, 추세추종, 외부 전략 기반으로 맞춤형 투자 개선 조언을 받아보세요
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              3단계로 시작하는 투자 분석
            </h3>
            <p className="text-gray-600 text-lg">
              복잡한 설정 없이 바로 시작하세요
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-900 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                1
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">거래 입력</h4>
              <p className="text-gray-600">
                종목명, 거래일, 가격, 수량을 입력하여 매매 내역을 기록합니다
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-slate-900 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                2
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">전략 선택</h4>
              <p className="text-gray-600">
                볼린저 밴드, 추세추종, 외부 전략 중 분석 방식을 선택합니다
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-slate-900 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                3
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">AI 분석 확인</h4>
              <p className="text-gray-600">
                AI가 생성한 맞춤형 투자 조언과 개선점을 확인합니다
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              데이터 기반 투자 습관 개선
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">실제 주가 데이터 연동</h4>
                  <p className="text-gray-600">Yahoo Finance API를 통해 정확한 시장 데이터 제공</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">다양한 분석 전략</h4>
                  <p className="text-gray-600">기술적 분석부터 외부 전략까지 폭넓은 관점 제공</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">투자 통계 대시보드</h4>
                  <p className="text-gray-600">총 투자금, 손익, 종목별 비중을 한눈에 확인</p>
                </div>
              </div>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden aspect-video shadow-lg">
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/DuqpiRs04e8?autoplay=1&mute=1&loop=1&playlist=DuqpiRs04e8&controls=0&rel=0&modestbranding=1"
              title="Inveskit 소개 영상"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Target className="w-16 h-16 mx-auto mb-6 opacity-80" />
          <h3 className="text-4xl font-bold mb-6">
            지금 바로 투자 습관을 개선하세요
          </h3>
          <p className="text-slate-300 text-lg mb-8 leading-relaxed">
            회원가입 없이 바로 시작할 수 있습니다.<br />
            AI 분석으로 더 나은 투자 결정을 내려보세요.
          </p>
          <a 
            href="https://www.inveskit.site" 
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-slate-900 px-10 py-4 rounded-lg font-bold text-lg hover:bg-slate-100 transition-all hover:scale-105 shadow-xl"
          >
            무료로 시작하기
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-gray-500 text-sm">
          <p>홍익대학교 AIDER</p>
        </div>
      </footer>
    </div>
  );
};

export default InveskitLanding;